/**
 * Импорт фото платья из CRM в «Наши фото»: чистая логика выбора окна
 * (reels/web/api_photos.py, `/api/crm-import/*`). Без браузерного кода — гоняется node-тестами.
 */

/** Группа по оценке «человек в кадре»: person ≥ 0.25, check — 0.12–0.25 или оценки нет, none — ниже. */
export type CrmGroup = "person" | "check" | "none";
export type CrmState = "new" | "imported" | "excluded" | "failed";

export type CrmPhoto = {
  media_id: number;
  url: string;
  kind: "main" | "mp" | "front" | "other";
  score: number | null;
  group: CrmGroup;
  state: CrmState;
  own_photo_id: number | null;
  thumb_path: string | null;
  error: string | null;
};

export const GROUPS: { key: CrmGroup; title: string; hint: string }[] = [
  { key: "person", title: "На человеке", hint: "Человек в кадре — отмечено по умолчанию" },
  { key: "check", title: "Проверить глазами", hint: "Оценка неуверенная или её нет" },
  { key: "none", title: "Без человека: вешалка, манекен, предметка", hint: "Человека в кадре нет" },
];

export type CrmSections = {
  fresh: Record<CrmGroup, CrmPhoto[]>;
  excluded: CrmPhoto[];
  failed: CrmPhoto[];
  imported: CrmPhoto[];
};

/** Раскладка по состояниям; новые — ещё и по группам. Порядок сервера сохраняется. */
export function sections(photos: CrmPhoto[]): CrmSections {
  const out: CrmSections = { fresh: { person: [], check: [], none: [] }, excluded: [], failed: [], imported: [] };
  for (const ph of photos) {
    if (ph.state === "new") out.fresh[ph.group].push(ph);
    else out[ph.state].push(ph);
  }
  return out;
}

/** Отмечены новые и упавшие фото группы «на человеке». */
export function initialChecked(photos: CrmPhoto[]): Set<number> {
  return new Set(
    photos.filter((ph) => ph.group === "person" && (ph.state === "new" || ph.state === "failed")).map((ph) => ph.media_id),
  );
}

/** Новый Set с включёнными/снятыми `ids`; вход не меняется. */
export function setMany(checked: Set<number>, ids: number[], on: boolean): Set<number> {
  const next = new Set(checked);
  for (const id of ids) {
    if (on) next.add(id);
    else next.delete(id);
  }
  return next;
}

/**
 * Тело импорта: отмеченные new/failed/excluded — в `import_ids`; неотмеченные new/failed — в
 * `exclude_ids`. Исключённые раньше без галочки и уже импортированные не попадают никуда.
 */
export function importBody(
  photos: CrmPhoto[],
  checked: Set<number>,
  dressId: number | null,
): { dress_id: number | null; import_ids: number[]; exclude_ids: number[] } {
  const import_ids: number[] = [];
  const exclude_ids: number[] = [];
  for (const ph of photos) {
    if (ph.state === "imported") continue;
    if (checked.has(ph.media_id)) import_ids.push(ph.media_id);
    else if (ph.state === "new" || ph.state === "failed") exclude_ids.push(ph.media_id);
  }
  return { dress_id: dressId, import_ids, exclude_ids };
}
